import type { Site } from "@/shared/model/user/UserDomains.ts";
import type { TimeManagementSitePreferences } from "@/pages/human-resources/time-management/business/preferences/TimeManagementSitePreferences.ts";
import type { GenericPreferences } from "@/shared/model/user/GenericPreferences.ts";
import type { ShiftPeriod } from "@/pages/human-resources/time-management/business/preferences/ShiftPeriod.ts";
import { ScheduleTemplateTime } from "@/pages/human-resources/time-management/business/schedule-template/ScheduleTemplateTime.ts";
import type { SelectedIndicator } from "@/pages/human-resources/time-management/business/preferences/SelectedIndicator.ts";

export default function parsePreferences(
  site: Site,
): TimeManagementSitePreferences {
  const rawValues = site.preferences.rawValues;
  return {
    shiftPeriods: parseShiftPeriods(rawValues),
    shouldComputeNbMeals: parseShouldComputeNbMeals(rawValues),
    breakDuration: parseBreakDuration(rawValues),
    canCreateBreakTimeSlots: parseBreakTimeSlotsAllowed(rawValues),
    selectedIndicators: parseSelectedIndicators(rawValues),
    rcrEnabled: parseRcrEnabled(rawValues),
  };
}

// -----------------------------------------------------------------------------
// Shifts periods
// -----------------------------------------------------------------------------
function parseShiftPeriods(prefs: GenericPreferences): ShiftPeriod[] {
  return (
    prefs.parseJson("com.netresto.rh.core.PNG_SERVICES", mapShiftPeriods) ?? []
  );
}

function mapShiftPeriods(json: unknown): ShiftPeriod[] {
  interface ShiftPeriodJson {
    libelle: string;
    heureDebut: string;
    heureFin: string;
    presenceDebut: string;
    presenceFin: string;
    couleur: string;
  }
  return (json as ShiftPeriodJson[]).map((js) => {
    const { start, end } = parseStartEndTimes(js.heureDebut, js.heureFin);
    const { start: attendanceStart, end: attendanceEnd } = parseStartEndTimes(
      js.presenceDebut,
      js.presenceFin,
    );
    return {
      start,
      end,
      attendanceStart,
      attendanceEnd,
      label: js.libelle,
      color: js.couleur,
    };
  });
}

function parseStartEndTimes(
  startTime: string,
  endTime: string,
): { start: ScheduleTemplateTime; end: ScheduleTemplateTime } {
  const start = parseLegacyTimeString(startTime);
  const end = ScheduleTemplateTime.correctedEndTime(
    start,
    parseLegacyTimeString(endTime),
  );
  return {
    start,
    end,
  };
}

function parseLegacyTimeString(time: string): ScheduleTemplateTime {
  if (!/\d{2}:\d{2}/.test(time)) {
    throw new Error(`Failed to parse time string: "${time}"`);
  }
  const [hour, minute] = time.split(":").map((s) => parseInt(s));
  return new ScheduleTemplateTime(hour, minute);
}

// -----------------------------------------------------------------------------
// Are we allowed to create time slots with category BREAK ?
// -----------------------------------------------------------------------------
function parseBreakTimeSlotsAllowed(prefs: GenericPreferences): boolean {
  return (
    prefs.parseBoolean("com.netresto.rh.core.ACTIVATE_PAUSE_ON_TRANCHE") ??
    false
  );
}

// -----------------------------------------------------------------------------
// Should we compute nb meals automatically ?
// -----------------------------------------------------------------------------
function parseShouldComputeNbMeals(prefs: GenericPreferences): boolean {
  return prefs.parseBoolean("com.netresto.rh.core.PNG_COMPUTE_REPAS") ?? true;
}

// -----------------------------------------------------------------------------
// Break duration
// -----------------------------------------------------------------------------
function parseBreakDuration(prefs: GenericPreferences): number {
  return prefs.parseNumber("com.netresto.rh.core.PNG_TEMPS_PAUSE") ?? 30;
}

// -----------------------------------------------------------------------------
// RCR enabled
// -----------------------------------------------------------------------------

function parseRcrEnabled(prefs: GenericPreferences): boolean {
  return prefs.parseBoolean("com.netresto.rh.core.ACTIVATE_RCR") ?? false;
}

// -----------------------------------------------------------------------------
// Selected schedule indicators
// -----------------------------------------------------------------------------
export function parseSelectedIndicators(
  prefs: GenericPreferences,
): SelectedIndicator[] {
  return (
    prefs.parseJson("com.netresto.rh.core.INDICATORS", mapSelectedIndicators) ??
    []
  );
}

function mapSelectedIndicators(json: unknown): SelectedIndicator[] {
  interface IndicatorPreference {
    provider: { id: string };
    indicator: { id?: string; idTimeSlotCategory?: string };
  }

  return (json as IndicatorPreference[]).map((p) => {
    const groupId = p.provider.id;
    const indicatorId = p.indicator.id ?? p.indicator.idTimeSlotCategory;
    if (indicatorId === undefined) {
      throw new Error(
        `Indicator in preferences has no id: ${JSON.stringify(p)}`,
      );
    }
    return { groupId, indicatorId };
  });
}
